import React, { cloneElement, createElement, memo, useContext, useLayoutEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { ChildDescriptorsContext } from "../contexts/ChildDescriptorsContext.js";
import { EditorContext } from "../contexts/EditorContext.js";
import { useClientOnly } from "../hooks/useClientOnly.js";
import { useNodeViewDescriptor } from "../hooks/useNodeViewDescriptor.js";
import { ChildNodeViews, wrapInDeco } from "./ChildNodeViews.js";
export const CustomNodeView = /*#__PURE__*/ memo(function CustomNodeView(param) {
    let { customNodeView, node, getPos, innerDeco, outerDeco } = param;
    const { view } = useContext(EditorContext);
    const domRef = useRef(null);
    const nodeDomRef = useRef(null);
    const contentDomRef = useRef(null);
    const getPosFunc = useRef(()=>getPos.current()).current;
    // this is ill-conceived; should revisit
    const initialNode = useRef(node);
    const initialOuterDeco = useRef(outerDeco);
    const initialInnerDeco = useRef(innerDeco);
    const customNodeViewRootRef = useRef(null);
    const customNodeViewRef = useRef(null);
    const shouldRender = useClientOnly();
    useLayoutEffect(()=>{
        if (!customNodeViewRef.current || !customNodeViewRootRef.current || !shouldRender) return;
        const { dom } = customNodeViewRef.current;
        nodeDomRef.current = customNodeViewRootRef.current;
        customNodeViewRootRef.current.appendChild(dom);
        return ()=>{
            customNodeViewRef.current?.destroy?.();
        };
    }, [
        customNodeViewRef,
        customNodeViewRootRef,
        nodeDomRef,
        shouldRender
    ]);
    useLayoutEffect(()=>{
        if (!customNodeView || !customNodeViewRef.current || !shouldRender) return;
        const { destroy, update } = customNodeViewRef.current;
        const updated = update?.call(customNodeViewRef.current, node, outerDeco, innerDeco) ?? true;
        if (updated) return;
        destroy?.call(customNodeViewRef.current);
        if (!customNodeViewRootRef.current) return;
        initialNode.current = node;
        initialOuterDeco.current = outerDeco;
        initialInnerDeco.current = innerDeco;
        customNodeViewRef.current = customNodeView(initialNode.current, // customNodeView will only be set if view is set, and we can only reach
        // this line if customNodeView is set
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        view, getPosFunc, initialOuterDeco.current, initialInnerDeco.current);
        const { dom } = customNodeViewRef.current;
        nodeDomRef.current = customNodeViewRootRef.current;
        customNodeViewRootRef.current.appendChild(dom);
    }, [
        customNodeView,
        view,
        innerDeco,
        node,
        outerDeco,
        getPos,
        customNodeViewRef,
        customNodeViewRootRef,
        initialNode,
        initialOuterDeco,
        initialInnerDeco,
        nodeDomRef,
        shouldRender,
        getPosFunc
    ]);
    const { childDescriptors, nodeViewDescRef } = useNodeViewDescriptor(node, ()=>getPos.current(), domRef, nodeDomRef, innerDeco, outerDeco, undefined, contentDomRef);
    const childContextValue = useMemo(()=>({
            parentRef: nodeViewDescRef,
            siblingsRef: childDescriptors
        }), [
        childDescriptors,
        nodeViewDescRef
    ]);
    if (!shouldRender) return null;
    if (!customNodeViewRef.current) {
        customNodeViewRef.current = customNodeView(initialNode.current, // customNodeView will only be set if view is set, and we can only reach
        // this line if customNodeView is set
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        view, ()=>getPos.current(), initialOuterDeco.current, initialInnerDeco.current);
    }
    const { contentDOM } = customNodeViewRef.current;
    contentDomRef.current = contentDOM ?? null;
    const element = /*#__PURE__*/ createElement(node.isInline ? "span" : "div", {
        ref: customNodeViewRootRef,
        contentEditable: !!contentDOM,
        suppressContentEditableWarning: true
    }, contentDOM && /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(ChildNodeViews, {
        getPos: getPos,
        node: node,
        innerDecorations: innerDeco
    }), contentDOM));
    const decoratedElement = /*#__PURE__*/ cloneElement(outerDeco.reduce(wrapInDeco, element), // eslint-disable-next-line @typescript-eslint/no-explicit-any
    outerDeco.some((d)=>d.type.attrs.nodeName) ? {
        ref: domRef
    } : // we've already passed the domRef to the NodeView component
    // as a prop
    undefined);
    return /*#__PURE__*/ React.createElement(ChildDescriptorsContext.Provider, {
        value: childContextValue
    }, decoratedElement);
});
